import React from 'react';
import {
  FaSearch,
  FaProjectDiagram,
  FaCode,
  FaCheckCircle,
  FaRocket,
  FaHeadset
} from 'react-icons/fa';

const HowWeWork: React.FC = () => {
  const process = [
    {
      step: '01',
      title: 'Discovery & Technical Assessment',
      icon: <FaSearch className="text-3xl" />,
      description: 'We start by understanding your business goals, technical challenges, and constraints. Our senior engineers conduct a thorough technical assessment to identify opportunities and risks.',
      activities: [
        'Stakeholder interviews and requirements gathering',
        'Existing system architecture review',
        'Technical feasibility analysis',
        'Risk assessment and mitigation planning',
        'Project scope definition and estimation'
      ],
      deliverable: 'Technical proposal with architecture recommendations and project roadmap'
    },
    {
      step: '02',
      title: 'Architecture & Planning',
      icon: <FaProjectDiagram className="text-3xl" />,
      description: 'Our architects design robust, scalable solutions aligned with industry best practices. We create detailed technical specifications and project plans with clear milestones.',
      activities: [
        'System architecture design and documentation',
        'Technology stack selection and justification',
        'Database schema and data model design',
        'API contract definition',
        'Sprint planning and milestone definition',
        'Team composition and resource allocation'
      ],
      deliverable: 'Architecture documents, technical specifications, and sprint backlog'
    },
    {
      step: '03',
      title: 'Agile Development & Iteration',
      icon: <FaCode className="text-3xl" />,
      description: 'We follow an iterative development process with regular demos and feedback loops. Transparent communication ensures you\'re never surprised by progress or challenges.',
      activities: [
        'Sprint-based development (1-2 week cycles)',
        'Daily standups and progress updates',
        'Code reviews and pair programming',
        'Continuous integration and automated testing',
        'Regular demo sessions with stakeholders',
        'Sprint retrospectives for continuous improvement'
      ],
      deliverable: 'Working software increments with each sprint, deployed to staging environments'
    },
    {
      step: '04',
      title: 'Quality Assurance & Testing',
      icon: <FaCheckCircle className="text-3xl" />,
      description: 'Quality is built into every stage through automated testing, code reviews, and comprehensive QA processes. We catch issues early and ensure production readiness.',
      activities: [
        'Unit and integration test development',
        'Automated end-to-end testing',
        'Performance and load testing',
        'Security vulnerability scanning',
        'User acceptance testing (UAT) coordination',
        'Bug fixing and regression testing'
      ],
      deliverable: 'Fully tested software with comprehensive test coverage reports'
    },
    {
      step: '05',
      title: 'Deployment & Go-Live',
      icon: <FaRocket className="text-3xl" />,
      description: 'We handle the entire deployment process, from environment setup to production release. Our DevOps practices ensure smooth, zero-downtime deployments.',
      activities: [
        'Production environment configuration',
        'Deployment automation and CI/CD setup',
        'Database migration execution',
        'Production deployment with rollback plan',
        'Post-deployment monitoring and validation',
        'Knowledge transfer and documentation'
      ],
      deliverable: 'Live production system with monitoring, documentation, and handover materials'
    },
    {
      step: '06',
      title: 'Support & Continuous Improvement',
      icon: <FaHeadset className="text-3xl" />,
      description: 'Our partnership doesn\'t end at deployment. We provide ongoing support, monitoring, and continuous improvements to ensure long-term success.',
      activities: [
        '24/7 production monitoring and alerts',
        'Bug fixes and emergency support',
        'Performance optimization',
        'Security patches and updates',
        'Feature enhancements and iterations',
        'Regular health checks and reviews'
      ],
      deliverable: 'Maintained and evolved system with ongoing support and improvements'
    }
  ];

  return (
    <section id="process" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            How We Work
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We follow a transparent, iterative development process with clear milestones, regular demos, and measurable outcomes.
            No surprises, no black boxes — just professional software development.
          </p>
        </div>

        {/* Process Timeline */}
        <div className="relative">
          {/* Vertical Line */}
          <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 bg-gradient-to-b from-blue-400 to-blue-600 h-full"></div>

          {/* Process Steps */}
          <div className="space-y-12">
            {process.map((item, index) => (
              <div
                key={index}
                className={`flex flex-col md:flex-row items-center gap-8 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Content Card */}
                <div className="flex-1">
                  <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="bg-blue-100 p-4 rounded-lg text-blue-600">
                        {item.icon}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-blue-600 mb-1">
                          STEP {item.step}
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900">
                          {item.title}
                        </h3>
                      </div>
                    </div>

                    <p className="text-gray-600 mb-6">
                      {item.description}
                    </p>

                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Key Activities:</h4>
                      <div className="grid grid-cols-1 gap-2">
                        {item.activities.map((activity, idx) => (
                          <div key={idx} className="flex items-start space-x-2">
                            <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-700 text-sm">{activity}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-600">
                      <div className="font-semibold text-gray-900 mb-1">Deliverable:</div>
                      <div className="text-gray-700 text-sm">{item.deliverable}</div>
                    </div>
                  </div>
                </div>

                {/* Center Circle */}
                <div className="hidden md:block relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-xl">{item.step}</span>
                  </div>
                </div>

                {/* Spacer for alternating layout */}
                <div className="hidden md:block flex-1"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center p-6 bg-white rounded-xl shadow-lg">
            <div className="text-4xl font-bold text-blue-600 mb-2">2-Week</div>
            <div className="text-gray-900 font-semibold mb-2">Sprint Cycles</div>
            <div className="text-gray-600 text-sm">
              Regular iterations with demos and feedback
            </div>
          </div>

          <div className="text-center p-6 bg-white rounded-xl shadow-lg">
            <div className="text-4xl font-bold text-blue-600 mb-2">100%</div>
            <div className="text-gray-900 font-semibold mb-2">Transparent</div>
            <div className="text-gray-600 text-sm">
              Full visibility into progress, blockers, and decisions
            </div>
          </div>

          <div className="text-center p-6 bg-white rounded-xl shadow-lg">
            <div className="text-4xl font-bold text-blue-600 mb-2">Agile</div>
            <div className="text-gray-900 font-semibold mb-2">Methodology</div>
            <div className="text-gray-600 text-sm">
              Flexible and adaptive to changing requirements
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowWeWork;
